// FIX-48: dark is this app's original/default look - every value here matches
// what used to be the single hardcoded `tokens` object. `lightTokens` is the
// new counterpart applied by ThemeProvider when the user switches modes.
//
// Scope note: these tokens only repaint the surfaces that actually read them
// via CSS var (globals.css's `:root` consumers like `.card`/`.glass`, plus
// app/layout.tsx's body background). Most page-level components in this app
// set colors with hardcoded inline styles (e.g. Navbar.tsx's
// `backgroundColor: '#070812'`) rather than these vars, so toggling theme
// does not re-skin the whole app today - widening coverage beyond the shared
// chrome is a follow-up, not part of this fix.
const darkTokens = {
  '--color-primary': '#00d4ff',
  '--color-secondary': '#5b7fff',
  '--color-accent': '#7c3aed',
  '--color-bg-dark': '#0a0f1c',
  '--color-bg-mid': '#101827',
  '--color-bg-light': '#111827',
  '--color-card': 'rgba(17,24,39,0.78)',
  '--color-card-border': 'rgba(91,127,255,0.18)',
  '--glass-bg': 'rgba(16,24,39,0.55)',
  '--glass-blur': '18px',
  '--muted-text': '#94a3b8',
  '--text-base': '#f1f5f9',
  // Design-system pass: badge/verified accent, toggled like every other
  // surface color above. (Glow shadows - --glow-cyan/--glow-purple/
  // --glow-teal - stay static in globals.css :root, matching the existing
  // pattern: glows aren't part of the light/dark swap, only colors are.)
  '--color-teal': '#2dd4bf',
} as const;

const lightTokens: Record<keyof typeof darkTokens, string> = {
  '--color-primary': '#0090cc',
  '--color-secondary': '#445dcc',
  '--color-accent': '#6d28d9',
  '--color-bg-dark': '#f4f7fc',
  '--color-bg-mid': '#eaf0fb',
  '--color-bg-light': '#ffffff',
  '--color-card': 'rgba(255,255,255,0.86)',
  '--color-card-border': 'rgba(91,127,255,0.18)',
  '--glass-bg': 'rgba(255, 255, 255, 0.7)',
  '--glass-blur': '18px',
  '--muted-text': '#5b6b85',
  '--text-base': '#0f172a',
  '--color-teal': '#0f9488',
};

const theme = {
  colors: {
    primary: '#00d4ff',
    secondary: '#5b7fff',
    accent: '#7c3aed',
    accent2: '#a855f7',
    teal: '#2dd4bf',
    bgDark: '#0a0f1c',
    bgMid: '#101827',
    bgLight: '#111827',
    card: 'rgba(17,24,39,0.78)',
    cardBorder: 'rgba(91,127,255,0.18)',
    success: '#22c55e',
    warning: '#f59e42',
    danger: '#ef4444',
    info: '#00a3ff',
  },
  // Back-compat: existing callers (e.g. the per-page ThemeProvider mounts in
  // app/page.tsx, app/dashboard/page.tsx, app/charities/**, app/wallet-socials/**)
  // read `theme.tokens` directly and expect the original dark values.
  tokens: darkTokens,
  darkTokens,
  lightTokens,
} as const;

export default theme;
